import React from 'react';
import { Pressable, Text, StyleSheet, ViewStyle, TextStyle, ActivityIndicator } from 'react-native';
import { Link } from 'expo-router';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';
import { Ionicons } from '@expo/vector-icons';

export interface LinkButtonProps {
  title: string;
  href: string;
  variant?: 'primary' | 'secondary' | 'danger' | 'outline' | 'ghost' | 'text';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: keyof typeof Ionicons.glyphMap;
  rightIcon?: keyof typeof Ionicons.glyphMap;
  replace?: boolean;
  style?: ViewStyle;
  textStyle?: TextStyle;
}

export function LinkButton({
  title,
  href,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  replace = false,
  style,
  textStyle,
}: LinkButtonProps) {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const styles = makeStyles(tokens, utils);

  const variantStyle = styles[variant];
  const variantTextStyle = styles[`${variant}Text` as keyof typeof styles] as TextStyle;
  const sizeKey = `size${size.charAt(0).toUpperCase() + size.slice(1)}` as 'sizeSm' | 'sizeMd' | 'sizeLg';
  const sizeTextKey = `${sizeKey}Text` as 'sizeSmText' | 'sizeMdText' | 'sizeLgText';
  const sizeStyle = styles[sizeKey];
  const sizeTextStyle = styles[sizeTextKey] as TextStyle;
  const disabledStyle = (disabled || loading) ? styles.disabled : null;
  const fullWidthStyle = fullWidth ? styles.fullWidth : null;

  const iconColor = variant === 'outline' || variant === 'ghost' || variant === 'text'
    ? tokens.color.onSurface 
    : variant === 'primary' 
      ? tokens.color.onPrimary 
      : variant === 'secondary'
        ? tokens.color.onSecondary
        : tokens.color.onError;

  const iconSize = size === 'sm' ? 16 : size === 'md' ? 18 : 20;

  const content = (
    <Pressable
      disabled={disabled || loading}
      style={({ pressed }) => [
        styles.base,
        variantStyle,
        sizeStyle,
        fullWidthStyle,
        disabledStyle,
        pressed && !disabled && !loading && styles.pressed,
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator 
          size="small" 
          color={iconColor} 
        />
      ) : (
        <React.Fragment>
          {leftIcon && (
            <Ionicons 
              name={leftIcon} 
              size={iconSize} 
              color={iconColor} 
              style={styles.leftIcon}
            />
          )}
          <Text style={[variantTextStyle, sizeTextStyle, textStyle]}>
            {title}
          </Text>
          {rightIcon && (
            <Ionicons 
              name={rightIcon} 
              size={iconSize} 
              color={iconColor} 
              style={styles.rightIcon}
            />
          )}
        </React.Fragment>
      )}
    </Pressable>
  );

  if (disabled || loading) {
    return content;
  }

  return (
    <Link href={href} replace={replace} asChild>
      {content}
    </Link>
  );
}

function makeStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    base: {
      borderRadius: utils.radius.md,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
    },
    leftIcon: {
      marginRight: utils.spacing.xs,
    },
    rightIcon: {
      marginLeft: utils.spacing.xs,
    },
    // Sizes
    sizeSm: {
      paddingHorizontal: utils.spacing.md,
      paddingVertical: utils.spacing.sm,
      minHeight: 32,
    },
    sizeSmText: {
      fontSize: utils.typography.sizes.sm,
    },
    sizeMd: {
      paddingHorizontal: utils.spacing.lg,
      paddingVertical: utils.spacing.md,
      minHeight: 44,
    },
    sizeMdText: {
      fontSize: utils.typography.sizes.base,
    },
    sizeLg: {
      paddingHorizontal: utils.spacing.xl,
      paddingVertical: utils.spacing.lg,
      minHeight: 52,
    },
    sizeLgText: {
      fontSize: utils.typography.sizes.lg,
    },
    // Variants
    primary: {
      backgroundColor: tokens.color.primary,
    },
    primaryText: {
      color: tokens.color.onPrimary,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    secondary: {
      backgroundColor: tokens.color.secondary,
    },
    secondaryText: {
      color: tokens.color.onSecondary,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    danger: {
      backgroundColor: tokens.color.error,
    },
    dangerText: {
      color: tokens.color.onError,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    outline: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: tokens.color.border,
    },
    outlineText: {
      color: tokens.color.onSurface,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    ghost: {
      backgroundColor: 'transparent',
    },
    ghostText: {
      color: tokens.color.onSurface,
      fontWeight: utils.typography.weights.medium,
      fontFamily: utils.typography.body.fontFamily,
    },
    text: {
      backgroundColor: 'transparent',
    },
    textText: {
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.medium,
      fontFamily: utils.typography.body.fontFamily,
    },
    disabled: {
      opacity: 0.6,
    },
    pressed: {
      opacity: 0.8,
    },
    fullWidth: {
      width: '100%',
    },
  });
}

